import { useState, useEffect, useMemo } from "react";
import { useAuth } from "@/hooks/useAuth";
import { useEvents } from "@/hooks/useConflictData";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Shield, MapPin, AlertTriangle, Crosshair, Zap, Lock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Slider } from "@/components/ui/slider";
import { Badge } from "@/components/ui/badge";
import PricingDialog from "@/components/PricingDialog";

const eventWeights: Record<string, number> = {
  missile: 3,
  air_raid: 2.5,
  drone: 2,
  explosion: 2,
  movement: 1,
};

const riskLabels = [
  { min: 0, max: 1, label: "SAFE", color: "hsl(var(--success))", bg: "bg-success/20" },
  { min: 1, max: 2, label: "LOW", color: "hsl(var(--success))", bg: "bg-success/10" },
  { min: 2, max: 3, label: "MODERATE", color: "hsl(var(--warning))", bg: "bg-warning/20" },
  { min: 3, max: 4, label: "HIGH", color: "hsl(var(--danger))", bg: "bg-danger/20" },
  { min: 4, max: 5, label: "CRITICAL", color: "hsl(var(--danger))", bg: "bg-destructive/20" },
];

function haversineDistance(lat1: number, lng1: number, lat2: number, lng2: number): number {
  const R = 6371;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLon = ((lng2 - lng1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos((lat1 * Math.PI) / 180) * Math.cos((lat2 * Math.PI) / 180) * Math.sin(dLon / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

function getRisk(score: number) {
  return riskLabels.find((r) => score >= r.min && score < r.max) || riskLabels[4];
}

const PersonalSafetyRadar = ({ embedded }: { embedded?: boolean }) => {
  const [pricingOpen, setPricingOpen] = useState(false);
  const { user, isPremium } = useAuth();
  const { data: events } = useEvents(undefined, isPremium);
  const [locationInput, setLocationInput] = useState("");
  const [location, setLocation] = useState<{ lat: number; lng: number; name: string } | null>(null);
  const [radius, setRadius] = useState([300]);
  const [loading, setLoading] = useState(false);

  const resolveLocation = async () => {
    if (!locationInput.trim()) return;
    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke("geocode", {
        body: { query: locationInput },
      });
      if (error) throw error;
      if (data?.found) {
        setLocation({ lat: data.lat, lng: data.lng, name: data.name });
        toast.success(`📍 Location set: ${data.name}, ${data.country}`);
      } else {
        toast.error("Location not found. Try a different city name.");
      }
    } catch {
      toast.error("Failed to resolve location");
    }
    setLoading(false);
  };

  const useMyLocation = () => {
    if (!navigator.geolocation) {
      toast.error("Geolocation not supported by your browser");
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setLocation({ lat: pos.coords.latitude, lng: pos.coords.longitude, name: "My Location" });
        toast.success("📍 Using your current location");
      },
      () => toast.error("Unable to get your location")
    );
  };

  const analysis = useMemo(() => {
    if (!location || !events?.length) return null;

    const nearbyEvents = events
      .filter((e) => e.lat && e.lng)
      .map((e) => ({
        ...e,
        distance: haversineDistance(location.lat, location.lng, e.lat!, e.lng!),
      }))
      .filter((e) => e.distance <= radius[0])
      .sort((a, b) => a.distance - b.distance);

    const now = Date.now();
    let riskScore = 0;
    for (const e of nearbyEvents) {
      const weight = eventWeights[e.event_type] || 1;
      const hoursAgo = (now - new Date(e.occurred_at).getTime()) / 3600000;
      const recency = hoursAgo < 12 ? 1.5 : 1;
      const proximity = 1 - e.distance / radius[0];
      riskScore += weight * recency * proximity;
    }
    riskScore = Math.min(5, riskScore / Math.max(1, nearbyEvents.length / 3));

    const byType: Record<string, number> = {};
    nearbyEvents.forEach((e) => {
      byType[e.event_type] = (byType[e.event_type] || 0) + 1;
    });

    return { nearbyEvents, riskScore, byType };
  }, [location, events, radius]);

  const risk = analysis ? getRisk(analysis.riskScore) : null;

  if (!isPremium && !embedded) {
    return (
      <section className="border-b border-border">
        <div className="container mx-auto px-4 py-12">
          <div className="relative overflow-hidden rounded-xl border border-border bg-card p-8 text-center">
            <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-transparent" />
            <div className="relative">
              <Lock className="mx-auto mb-4 h-12 w-12 text-muted-foreground" />
              <h2 className="mb-2 text-2xl font-bold text-foreground">🛡️ Personal Safety Radar</h2>
              <p className="mb-6 text-muted-foreground">
                Set your location and get a real-time threat assessment within your chosen radius.
                See nearby missile strikes, drone attacks, and military movements.
              </p>
              <Button
                onClick={() => setPricingOpen(true)}
                className="bg-primary text-primary-foreground"
              >
                <Zap className="mr-2 h-4 w-4" /> Unlock Premium
              </Button>
            </div>
          </div>
        </div>
        <PricingDialog open={pricingOpen} onOpenChange={setPricingOpen} />
      </section>
    );
  }

  const Wrapper = embedded ? "div" : "section";
  const wrapperProps = embedded ? {} : { id: "safety-radar", className: "border-b border-border" };

  return (
    <Wrapper {...wrapperProps}>
      <div className={embedded ? "" : "container mx-auto px-4 py-8"}>
        {!embedded && (
        <div className="mb-6">
          <h2 className="text-2xl font-bold text-foreground flex items-center gap-2">
            <Shield className="h-6 w-6 text-primary" /> Personal Safety Radar
          </h2>
          <p className="text-sm text-muted-foreground">
            Real-time threat assessment around your location
          </p>
        </div>
        )}

        {/* Location Input */}
        <div className="mb-6 grid gap-4 md:grid-cols-2">
          <div className="rounded-lg border border-border bg-card p-4">
            <label className="mb-2 block text-xs font-medium uppercase text-muted-foreground">
              Your Location
            </label>
            <div className="flex gap-2">
              <Input
                value={locationInput}
                onChange={(e) => setLocationInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && resolveLocation()}
                placeholder="Enter city name (e.g. Kyiv, Tel Aviv)"
                className="bg-background"
              />
              <Button onClick={resolveLocation} disabled={loading} size="sm" variant="secondary">
                <MapPin className="mr-1 h-4 w-4" /> Set
              </Button>
              <Button onClick={useMyLocation} size="sm" variant="outline">
                <Crosshair className="h-4 w-4" />
              </Button>
            </div>
            {location && (
              <p className="mt-2 text-xs text-primary">
                📍 {location.name} ({location.lat.toFixed(2)}, {location.lng.toFixed(2)})
              </p>
            )}
          </div>

          <div className="rounded-lg border border-border bg-card p-4">
            <label className="mb-2 block text-xs font-medium uppercase text-muted-foreground">
              Monitoring Radius: {radius[0]} km
            </label>
            <Slider
              value={radius}
              onValueChange={setRadius}
              min={50}
              max={1000}
              step={50}
              className="mt-3"
            />
            <div className="mt-2 flex justify-between text-xs text-muted-foreground">
              <span>50 km</span>
              <span>1000 km</span>
            </div>
          </div>
        </div>

        {/* Radar Results */}
        {location && analysis && risk && (
          <div className="grid gap-4 md:grid-cols-3">
            {/* Risk Level */}
            <div
              className="rounded-lg border border-border bg-card p-6 text-center"
              style={{ borderColor: risk.color }}
            >
              <div className="mb-2 font-mono text-xs uppercase text-muted-foreground">
                Threat Level
              </div>
              <div className="text-4xl font-black" style={{ color: risk.color }}>
                {risk.label}
              </div>
              <div className="mt-1 font-mono text-sm text-muted-foreground">
                Score: {analysis.riskScore.toFixed(1)} / 5.0
              </div>
              <div className="mx-auto mt-3 h-2 w-full max-w-[200px] overflow-hidden rounded-full bg-secondary">
                <div
                  className="h-full rounded-full transition-all"
                  style={{
                    width: `${(analysis.riskScore / 5) * 100}%`,
                    background: risk.color,
                  }}
                />
              </div>
            </div>

            {/* Threats Count */}
            <div className="rounded-lg border border-border bg-card p-6">
              <div className="mb-3 font-mono text-xs uppercase text-muted-foreground">
                Threats within {radius[0]} km
              </div>
              <div className="text-3xl font-bold text-foreground">
                {analysis.nearbyEvents.length}
              </div>
              <div className="mt-3 flex flex-wrap gap-1">
                {Object.entries(analysis.byType).map(([type, count]) => (
                  <Badge key={type} variant="outline" className="font-mono text-xs">
                    {type}: {count}
                  </Badge>
                ))}
              </div>
            </div>

            {/* Nearest Threat */}
            <div className="rounded-lg border border-border bg-card p-6">
              <div className="mb-3 font-mono text-xs uppercase text-muted-foreground">
                Nearest Threat
              </div>
              {analysis.nearbyEvents.length > 0 ? (
                <>
                  <div className="flex items-center gap-2">
                    <AlertTriangle className="h-5 w-5 text-destructive" />
                    <span className="text-sm font-medium text-foreground">
                      {analysis.nearbyEvents[0].title.slice(0, 60)}...
                    </span>
                  </div>
                  <div className="mt-2 font-mono text-xs text-muted-foreground">
                    📏 {analysis.nearbyEvents[0].distance.toFixed(0)} km away
                  </div>
                  <div className="mt-1 text-xs text-muted-foreground">
                    {analysis.nearbyEvents[0].city || analysis.nearbyEvents[0].country}
                  </div>
                </>
              ) : (
                <div className="text-sm text-muted-foreground">
                  No threats detected within {radius[0]} km ✅
                </div>
              )}
            </div>
          </div>
        )}

        {/* Recent Threats List */}
        {location && analysis && analysis.nearbyEvents.length > 0 && (
          <div className="mt-4 rounded-lg border border-border bg-card p-4">
            <h3 className="mb-3 font-mono text-xs uppercase text-muted-foreground">
              Recent threats ({Math.min(analysis.nearbyEvents.length, 10)} of{" "}
              {analysis.nearbyEvents.length})
            </h3>
            <div className="space-y-2">
              {analysis.nearbyEvents.slice(0, 10).map((e) => (
                <div
                  key={e.id}
                  className="flex items-center justify-between rounded border border-border/50 bg-background px-3 py-2"
                >
                  <div className="flex items-center gap-3">
                    <Badge variant="outline" className="font-mono text-xs uppercase">
                      {e.event_type}
                    </Badge>
                    <span className="text-sm text-foreground">{e.title.slice(0, 50)}</span>
                  </div>
                  <span className="font-mono text-xs text-muted-foreground">
                    {e.distance.toFixed(0)} km
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {!location && (
          <div className="rounded-lg border border-dashed border-border bg-card/50 p-12 text-center">
            <Crosshair className="mx-auto mb-3 h-8 w-8 text-muted-foreground" />
            <p className="text-muted-foreground">
              Enter your location above to activate the Safety Radar
            </p>
          </div>
        )}
      </div>
    </Wrapper>
  );
};

export default PersonalSafetyRadar;
